/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {EventEmitter} from '@angular/core';

import {isPresent} from '../utils/lang';

import {DialogModal, ModalDefinition} from './interfaces';
import {ModalOutletComponent} from './modal-outlet.component';
import {MESSAGE_TYPE} from '../ui';
import {HttpErrorResponse} from '@angular/common/http';

/**
 * Modal service that exposes methods for opening modals, either by "id" (modals whose content exists as a component
 * in the app tree already (for example the aside menu is such a component, on smaller resolutions it is displayed as
 * a modal) or by "name", that is "view-like" components registered against the modal outlet (in a fashion similar to
 * that of the views registered against the router outlet). See the @ModalConfig on the root application component.
 * This services also exposes two event streams : one for when a modal is open (either by name or id) and one for when
 * a modal is closed.
 */
export class ModalService {

  // Event emitter that streams the identifiers of the modal that request to be opened
  public modalOpened: EventEmitter<string> = new EventEmitter<string>();

  // Event emitter that streams the identifiers of the modal that has been closed
  public modalClosed: EventEmitter<string> = new EventEmitter<string>();

  public modalWithIdOpen: Array<string> = [];

  private _outlet: ModalOutletComponent = null;

  private modals: Array<DialogModal> = [
    {
      'name': 'pending-changes',
      'title': 'DIALOG.save_changes',
      'text': 'DIALOG.save_changes?',
      'buttons': [
        {
          name: 'cancel',
          text: `FORMS.cancel`,
          style: 'dark-white'
        },
        {
          name: 'dismiss',
          text: `FORMS.dismiss`,
          style: 'dark-white'
        },
        {
          name: 'save',
          text: 'FORMS.save',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'dated-block-overlaps',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.dated-block-overlaps',
      'buttons': [
        {
          name: 'ok',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'dated-block-add-between',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.dated-block-add-between',
      'buttons': [
        {
          name: 'ok',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.yes',
          style: 'btn-dark'
        },
        {
          name: 'revert-dates',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      name: 'modify-dates-middle-occurrence',
      title: '',
      text: 'FORM_GENERATOR.MODALS.DIALOGS.modify-dates-middle-occurrence',
      'buttons': [
        {
          name: 'revert-dates',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.yes',
          style: 'btn-dark'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'dated-block-first-occurence-begindate-eternity',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.first-occurence-begindate-eternity',
      'buttons': [
        {
          name: 'add-minus-eternity',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'dated-block-last-occurence-enddate-eternity',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.last-occurence-enddate-eternity',
      'buttons': [
        {
          name: 'add-plus-eternity',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'dated-block-delete-handler',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.dated-block-delete-handler',
      'buttons': [
        {
          name: 'change-previous',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.change-previous',
          style: 'btn-dark'
        },
        {
          name: 'change-next',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.change-next',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'dated-block-delete',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.dated-block-delete',
      'buttons': [
        {
          name: 'delete-occurrence',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.delete-occurrence',
          style: 'danger'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'dated-block-clear',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.dated-block-clear',
      'buttons': [
        {
          name: 'clear-block',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.yes',
          style: 'danger'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'non-dated-block-clear',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.non-dated-block-clear',
      'buttons': [
        {
          name: 'clear-block',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.yes',
          style: 'danger'
        },
        {
          name: 'cancel',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.cancel',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'end-date-after-max',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.end-date-after-max',
      'buttons': [
        {
          name: 'ok',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'begin-date-before-min',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.begin-date-before-min',
      'buttons': [
        {
          name: 'ok',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'begin-date-before-contract-start',
      'title': '',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.begin-date-before-contract-start',
      'buttons': [
        {
          name: 'ok',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.ok',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'save_report',
      'title': '',
      'text': 'Do you want to update the existing report or create a new one?',
      'buttons': [
        {
          name: 'update',
          text: 'Update report',
          style: 'btn-dark'
        },
        {
          name: 'new',
          text: 'New report',
          style: 'dark-white'
        }
      ]
    },
    {
      'name': 'save_reduce_position_date',
      'title': '',
      'text': 'DIALOG.reduce_position_date',
      'buttons': [
        {
          name: 'need-check',
          text: 'DIALOG.need_check',
          style: 'dark-white'
        },
        {
          name: 'yes',
          text: 'FORM_GENERATOR.MODALS.BUTTONS.yes',
          style: 'btn-dark'
        }
      ]
    },
    {
      'name': 'confirm_profile_change',
      'title': 'FORM_GENERATOR.MODALS.DIALOGS.profile-change-warning-title',
      'text': 'FORM_GENERATOR.MODALS.DIALOGS.profile-change-warning-message',
      'buttons': [
        {
          name: 'cancel',
          text: `FORMS.cancel`,
          style: 'dark-white'
        },
        {
          name: 'save',
          text: 'FORMS.save',
          style: 'btn-dark'
        }
      ]
    }
  ];

  constructor(public config: ModalDefinition[]) {
    this.modalClosed.subscribe((idOrName: string) => {
      const index = this.modalWithIdOpen.indexOf(idOrName);
      if (index !== -1) {
        this.modalWithIdOpen.splice(index, 1);
      }
    });
  }

  /**
   * Register an outlet to be notified of modal activation
   */
  public registerOutlet(outlet: ModalOutletComponent): ModalDefinition[] {
    if (isPresent(this._outlet)) {
      // There can only by one modal outlet registered against this service.
      throw new Error('Modal outlet is already registered.');
    }

    this._outlet = outlet;
    return this.config;
  }

  public openModalWithId(id: string, once?: boolean) {
    if (!once || !this.modalWithIdOpen.includes(id)) {
      this.modalOpened.emit(id);
      this.modalWithIdOpen.push(id);
    }
  }

  public openModalWithName(name: string) {
    return this._outlet.openModal(name);
  }

  public closeTopMostModalFromOutlet() {
    this._outlet.closeTopmostModal();
  }

  public closeTopmostModal() {
    if (this.modalWithIdOpen.length > 0) {
      // First, look at modals open with an ID
      this.modalClosed.emit(this.modalWithIdOpen[this.modalWithIdOpen.length - 1]);
    } else {
      // Then, look at modals in the modal outlet
      this._outlet.closeTopmostModal();
    }
  }

  /**
   * Closes all modal popups
   */
  public closeAllModals() {
    if (this.modalWithIdOpen.length > 0) {
      this.modalWithIdOpen.forEach((modal) => {
        this.modalClosed.emit(modal);
      });
    }

    this._outlet.closeAllModals();
  }

  public alert(options: {
    title?: string;
    text?: string,
    size?: string,
    detail?: HttpErrorResponse | Error,
    customInfo?: {
      description?: string,
      message?: string
    },
    fancyDialogOptions?: {
      errorType?: MESSAGE_TYPE,
      description?: string
    },
  }): Promise<void> {
    return this._outlet.alert(options);
  }

  public dynamicConfirmModal(options: {
    title?: string;
    text?: string,
    size?: string,
    customClass?: string,
    confirmButton?: string,
    cancelButton?: string,
    icon?: string,
    isCrossButtonVisible?: boolean,
    variant?: 'danger' | 'default'
  }): Promise<boolean> {
    return this._outlet.dynamicConfirmModal(options);
  }

  public confirm(
    options: { title?: string; text?: string, size?: string, customClass?: string, type?: 'danger' | 'warning' }
  ): Promise<boolean> {
    return this._outlet.confirm(options);
  }

  public dialog(options: DialogModal): Promise<string> {
    return this._outlet.dialog(options);
  }

  public dialogFromData(name: string, title?: string, text?: string, note?: string) {
    const options = this.modals.find((item) => {
      return item.name === name;
    });

    if (options) {
      options.title = title || options.title;
      options.text = text || options.text;
      return this.dialog(options);
    }
  }

  public apiError(options: {
    type?: 'DANGER' | 'WARNING';
    title?: string;
    titleIcon?: string;
    description?: string,
    details?: string[],
    technicalDetailsForCopy?: string,
    withIllustration?: boolean
  }): Promise<void> {
    return this._outlet.apiError(options);
  }
}
